import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, InputNumber, message, Space, Tabs, Select, Row, Col, Switch } from 'antd';
import { SaveOutlined, ReloadOutlined, VideoCameraOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';

const FormList = Form.List;

const { TextArea } = Input;
const { TabPane } = Tabs;
const { Option } = Select;

// 直播配置接口
interface LiveStreamConfigData {
  // 主播收益分成比例
  anchorRevenueShareRatio: number; // 0-1
  // 赠送礼物真金兑换钻石比例
  giftGoldToDiamondRatio: number;
  // 短视频语言版本
  shortVideoLanguage: string; // 'vi' | 'zh' | 'en'
  // 欢迎语
  welcomeMessage: string;
  // 直播间公告
  liveRoomAnnouncementEnabled: boolean; // 是否启用直播间公告
  liveRoomAnnouncement: string;
}

// 直播付费配置接口
interface LiveStreamPaymentConfig {
  // 门票收费配置
  ticketPayment: {
    quickAmounts: number[]; // 快捷金额，最多6个
    minAmount: number; // 最小金额
    maxAmount: number; // 最大金额
    defaultAmount: number; // 默认金额
    freeWatchDuration: number; // 免费观看时长（秒）
  };
  // 计时收费配置
  timeBasedPayment: {
    quickAmounts: number[]; // 快捷金额，最多6个
    minAmount: number; // 最小金额
    maxAmount: number; // 最大金额
    defaultAmount: number; // 默认金额
    freeWatchDuration: number; // 免费观看时长（秒）
  };
}

const LiveStreamSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('live-config');
  const [configForm] = Form.useForm();
  const [paymentForm] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // 初始化数据
  useEffect(() => {
    loadConfig();
    loadPaymentConfig();
  }, []);

  // 加载直播配置
  const loadConfig = async () => {
    try {
      // 模拟API调用
      await new Promise((resolve) => setTimeout(resolve, 100));
      const mockConfig: LiveStreamConfigData = {
        anchorRevenueShareRatio: 0,
        giftGoldToDiamondRatio: 1,
        shortVideoLanguage: 'vi',
        welcomeMessage: 'Chào mừng <%s>',
        liveRoomAnnouncementEnabled: true,
        liveRoomAnnouncement: `Chào mừng bạn đến với AA.LIVE!
• Nạp lần đầu tặng thưởng lên đến 36%
• Mỗi lần nạp thêm tặng thưởng lên đến 5.118K
• VIP lên đến 18 triệu, lương hàng tuần
• Nền tảng cứu trợ tổn thất hôm nay
• Mời bạn bè nhận thưởng tiền mặt ngay lập tức`,
      };
      configForm.setFieldsValue(mockConfig);
    } catch (error) {
      message.error('加载配置失败');
    }
  };

  // 加载付费配置
  const loadPaymentConfig = async () => {
    try {
      // 模拟API调用
      await new Promise((resolve) => setTimeout(resolve, 100));
      const mockPaymentConfig: LiveStreamPaymentConfig = {
        ticketPayment: {
          quickAmounts: [100, 500, 1000, 5000],
          minAmount: 10,
          maxAmount: 10000,
          defaultAmount: 100,
          freeWatchDuration: 0,
        },
        timeBasedPayment: {
          quickAmounts: [50, 200, 500, 2000],
          minAmount: 10,
          maxAmount: 5000,
          defaultAmount: 50,
          freeWatchDuration: 0,
        },
      };
      // 设置表单值，确保quickAmounts是数组
      paymentForm.setFieldsValue({
        ticketPayment: {
          ...mockPaymentConfig.ticketPayment,
          quickAmounts: mockPaymentConfig.ticketPayment.quickAmounts || [],
        },
        timeBasedPayment: {
          ...mockPaymentConfig.timeBasedPayment,
          quickAmounts: mockPaymentConfig.timeBasedPayment.quickAmounts || [],
        },
      });
    } catch (error) {
      message.error('加载付费配置失败');
    }
  };

  // 保存直播配置
  const handleSaveConfig = async (values: LiveStreamConfigData) => {
    setLoading(true);
    try {
      // 模拟API调用
      await new Promise((resolve) => setTimeout(resolve, 1000));
      console.log('保存直播配置:', values);
      message.success('直播配置保存成功！');
    } catch (error) {
      message.error('保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 保存付费配置
  const handleSavePaymentConfig = async (values: LiveStreamPaymentConfig) => {
    setLoading(true);
    try {
      // 模拟API调用
      await new Promise((resolve) => setTimeout(resolve, 1000));
      console.log('保存付费配置:', values);
      message.success('付费配置保存成功！');
    } catch (error) {
      message.error('保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 重置直播配置
  const handleResetConfig = () => {
    configForm.resetFields();
    loadConfig();
    message.info('已重置为默认设置');
  };

  // 重置付费配置
  const handleResetPaymentConfig = () => {
    paymentForm.resetFields();
    loadPaymentConfig();
    message.info('已重置为默认设置');
  };

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <h2>
            <VideoCameraOutlined /> 直播设置
          </h2>
          <p style={{ color: '#666', marginTop: 8 }}>
            配置直播相关设置，包括直播配置、分类配置、用户等级配置和付费配置
          </p>
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {/* 直播配置 Tab */}
          <TabPane tab="直播配置" key="live-config">
            <Form
              form={configForm}
              layout="vertical"
              onFinish={handleSaveConfig}
              initialValues={{
                anchorRevenueShareRatio: 0,
                giftGoldToDiamondRatio: 1,
                shortVideoLanguage: 'vi',
                welcomeMessage: 'Chào mừng <%s>',
                liveRoomAnnouncementEnabled: true,
                liveRoomAnnouncement: '',
              }}
            >
              <Form.Item
                label="主播收益分成比例"
                name="anchorRevenueShareRatio"
                tooltip="比例系数(0-1)"
                rules={[
                  { required: true, message: '请输入主播收益分成比例' },
                  { type: 'number', min: 0, max: 1, message: '比例必须在0-1之间' },
                ]}
              >
                <InputNumber
                  min={0}
                  max={1}
                  step={0.01}
                  style={{ width: '100%' }}
                  placeholder="请输入比例系数(0-1)"
                />
              </Form.Item>
              <div style={{ color: '#999', marginTop: -12, marginBottom: 16, fontSize: 12 }}>
                说明:比例系数(0-1)
              </div>

              <Form.Item
                label="赠送礼物真金兑换钻石比例"
                name="giftGoldToDiamondRatio"
                rules={[
                  { required: true, message: '请输入兑换比例' },
                  { type: 'number', min: 0, message: '比例必须大于0' },
                ]}
              >
                <InputNumber
                  min={0}
                  step={0.1}
                  style={{ width: '100%' }}
                  placeholder="请输入兑换比例"
                />
              </Form.Item>

              <Form.Item
                label="短视频语言版本"
                name="shortVideoLanguage"
                rules={[{ required: true, message: '请选择语言版本' }]}
              >
                <Select placeholder="请选择语言版本" style={{ width: '100%' }}>
                  <Option value="vi">越南语</Option>
                  <Option value="zh">中文</Option>
                  <Option value="en">英语</Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="欢迎语"
                name="welcomeMessage"
                rules={[
                  { required: true, message: '请输入欢迎语' },
                  { max: 200, message: '欢迎语不能超过200个字符' },
                ]}
              >
                <Input
                  placeholder="请输入欢迎语，支持变量：<%s> 表示用户名"
                  maxLength={200}
                />
              </Form.Item>

              <Form.Item
                label="直播间公告"
                name="liveRoomAnnouncementEnabled"
                valuePropName="checked"
              >
                <Switch checkedChildren="启用" unCheckedChildren="关闭" />
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.liveRoomAnnouncementEnabled !== currentValues.liveRoomAnnouncementEnabled
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue('liveRoomAnnouncementEnabled') ? (
                    <Form.Item
                      label="公告内容"
                      name="liveRoomAnnouncement"
                      rules={[{ max: 2000, message: '公告内容不能超过2000个字符' }]}
                    >
                      <TextArea
                        rows={8}
                        placeholder="请输入直播间公告内容"
                        showCount
                        maxLength={2000}
                      />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>

              {/* 操作按钮 */}
              <div style={{ textAlign: 'right', marginTop: 24 }}>
                <Space>
                  <Button icon={<ReloadOutlined />} onClick={handleResetConfig}>
                    重置
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    htmlType="submit"
                    loading={loading}
                  >
                    保存
                  </Button>
                </Space>
              </div>
            </Form>
          </TabPane>

          {/* 直播分类配置 Tab */}
          <TabPane tab="直播分类配置" key="category-config">
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
              直播分类配置功能开发中...
            </div>
          </TabPane>

          {/* 直播用户等级配置 Tab */}
          <TabPane tab="直播用户等级配置" key="user-level-config">
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
              直播用户等级配置功能开发中...
            </div>
          </TabPane>

          {/* 真人视讯好路推荐 Tab */}
          <TabPane tab="真人视讯好路推荐" key="good-road-recommend">
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
              真人视讯好路推荐功能开发中...
            </div>
          </TabPane>

          {/* 付费直播间配置 Tab */}
          <TabPane tab="付费直播间配置" key="payment-config">
            <Form
              form={paymentForm}
              layout="vertical"
              onFinish={handleSavePaymentConfig}
            >
              {/* 按场付费配置 */}
              <Card
                title="按场付费配置"
                size="small"
                style={{ marginBottom: 24 }}
              >
                <div style={{ marginBottom: 16, padding: 12, background: '#f0f7ff', borderRadius: 4, border: '1px solid #91d5ff' }}>
                  <div style={{ color: '#1890ff', fontWeight: 500, marginBottom: 8 }}>配置说明：</div>
                  <div style={{ color: '#666', fontSize: 12, lineHeight: 1.8 }}>
                    <div>• <strong>快捷金额</strong>：开播时可点击选择，最多4个</div>
                    <div>• <strong>上限/下限金额</strong>：手动输入金额的范围限制</div>
                    <div>• <strong>默认金额</strong>：开播时默认显示的金额</div>
                    <div>• <strong>免费观看时长</strong>：每个用户可免费观看的时长（秒），消耗完后需付费</div>
                  </div>
                </div>

                <Form.Item
                  label="快捷金额（最多4个，可不配置）"
                  tooltip="主播开播时可以直接点击选择的金额，不用手动输入"
                >
                  <FormList name={['ticketPayment', 'quickAmounts']}>
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...restField }) => (
                          <Row key={key} gutter={8} style={{ marginBottom: 8 }}>
                            <Col flex="auto">
                              <Form.Item
                                {...restField}
                                name={name}
                                rules={[
                                  { required: true, message: '请输入金额' },
                                  { type: 'number', min: 0, message: '金额必须大于等于0' },
                                ]}
                                noStyle
                              >
                                <InputNumber
                                  min={0}
                                  style={{ width: '100%' }}
                                  placeholder="请输入快捷金额"
                                />
                              </Form.Item>
                            </Col>
                            <Col>
                              <Button
                                type="link"
                                danger
                                icon={<MinusCircleOutlined />}
                                onClick={() => remove(name)}
                              >
                                删除
                              </Button>
                            </Col>
                          </Row>
                        ))}
                        {fields.length < 4 && (
                          <Button
                            type="dashed"
                            onClick={() => add()}
                            block
                            icon={<PlusOutlined />}
                          >
                            添加快捷金额
                          </Button>
                        )}
                      </>
                    )}
                  </FormList>
                </Form.Item>

                <Form.Item
                  label="下限金额"
                  name={['ticketPayment', 'minAmount']}
                  tooltip="主播手动输入金额时，不能低于此下限"
                  rules={[
                    { required: true, message: '请输入下限金额' },
                    { type: 'number', min: 0, message: '下限金额必须大于等于0' },
                  ]}
                >
                  <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  placeholder="请输入下限金额"
                />
                </Form.Item>

                <Form.Item
                  label="上限金额"
                  name={['ticketPayment', 'maxAmount']}
                  tooltip="主播手动输入金额时，不能超过此上限"
                  rules={[
                    { required: true, message: '请输入上限金额' },
                    { type: 'number', min: 0, message: '上限金额必须大于等于0' },
                  ]}
                >
                  <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    placeholder="请输入上限金额"
                  />
                </Form.Item>

                <Form.Item
                  label="默认金额"
                  name={['ticketPayment', 'defaultAmount']}
                  tooltip="主播进入开播，选择按场付费后直接获取的金额，可以修改"
                  rules={[
                    { required: true, message: '请输入默认金额' },
                    { type: 'number', min: 0, message: '默认金额必须大于等于0' },
                  ]}
                >
                  <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    placeholder="请输入默认金额"
                  />
                </Form.Item>

                <Form.Item
                  label="免费观看时长（秒）"
                  name={['ticketPayment', 'freeWatchDuration']}
                  tooltip="每个用户可免费观看的时长（秒），消耗完后需付费，重新进入或清缓存也无法免费观看"
                  rules={[
                    { required: true, message: '请输入免费观看时长' },
                    { type: 'number', min: 0, message: '免费观看时长必须大于等于0' },
                  ]}
                >
                  <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    placeholder="请输入免费观看时长（秒）"
                    addonAfter="秒"
                  />
                </Form.Item>
              </Card>

              {/* 按时付费配置 */}
              <Card
                title="按时付费配置"
                size="small"
                style={{ marginBottom: 24 }}
              >
                <div style={{ marginBottom: 16, padding: 12, background: '#f0f7ff', borderRadius: 4, border: '1px solid #91d5ff' }}>
                  <div style={{ color: '#1890ff', fontWeight: 500, marginBottom: 8 }}>配置说明：</div>
                  <div style={{ color: '#666', fontSize: 12, lineHeight: 1.8 }}>
                    <div>• <strong>快捷金额</strong>：开播时可点击选择，最多4个</div>
                    <div>• <strong>上限/下限金额</strong>：手动输入金额的范围限制</div>
                    <div>• <strong>默认金额</strong>：开播时默认显示的金额</div>
                    <div>• <strong>免费观看时长</strong>：每个用户可免费观看的时长（秒），消耗完后需付费</div>
                  </div>
                </div>

                <Form.Item
                  label="快捷金额（最多4个，可不配置）"
                  tooltip="主播开播时可以直接点击选择的金额，不用手动输入"
                >
                  <FormList name={['timeBasedPayment', 'quickAmounts']}>
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...restField }) => (
                          <Row key={key} gutter={8} style={{ marginBottom: 8 }}>
                            <Col flex="auto">
                              <Form.Item
                                {...restField}
                                name={name}
                                rules={[
                                  { required: true, message: '请输入金额' },
                                  { type: 'number', min: 0, message: '金额必须大于等于0' },
                                ]}
                                noStyle
                              >
                                <InputNumber
                                  min={0}
                                  style={{ width: '100%' }}
                                  placeholder="请输入快捷金额"
                                />
                              </Form.Item>
                            </Col>
                            <Col>
                              <Button
                                type="link"
                                danger
                                icon={<MinusCircleOutlined />}
                                onClick={() => remove(name)}
                              >
                                删除
                              </Button>
                            </Col>
                          </Row>
                        ))}
                        {fields.length < 4 && (
                          <Button
                            type="dashed"
                            onClick={() => add()}
                            block
                            icon={<PlusOutlined />}
                          >
                            添加快捷金额
                          </Button>
                        )}
                      </>
                    )}
                  </FormList>
                </Form.Item>

                <Form.Item
                  label="下限金额"
                  name={['timeBasedPayment', 'minAmount']}
                  tooltip="主播手动输入金额时，不能低于此下限"
                  rules={[
                    { required: true, message: '请输入下限金额' },
                    { type: 'number', min: 0, message: '下限金额必须大于等于0' },
                  ]}
                >
                  <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    placeholder="请输入下限金额"
                  />
                </Form.Item>

                <Form.Item
                  label="上限金额"
                  name={['timeBasedPayment', 'maxAmount']}
                  tooltip="主播手动输入金额时，不能超过此上限"
                  rules={[
                    { required: true, message: '请输入上限金额' },
                    { type: 'number', min: 0, message: '上限金额必须大于等于0' },
                  ]}
                >
                  <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    placeholder="请输入上限金额"
                  />
                </Form.Item>

                <Form.Item
                  label="默认金额"
                  name={['timeBasedPayment', 'defaultAmount']}
                  tooltip="主播进入开播，选择按时付费后直接获取的金额，可以修改"
                  rules={[
                    { required: true, message: '请输入默认金额' },
                    { type: 'number', min: 0, message: '默认金额必须大于等于0' },
                  ]}
                >
                  <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    placeholder="请输入默认金额"
                  />
                </Form.Item>

                <Form.Item
                  label="免费观看时长（秒）"
                  name={['timeBasedPayment', 'freeWatchDuration']}
                  tooltip="每个用户可免费观看的时长（秒），消耗完后需付费，重新进入或清缓存也无法免费观看"
                  rules={[
                    { required: true, message: '请输入免费观看时长' },
                    { type: 'number', min: 0, message: '免费观看时长必须大于等于0' },
                  ]}
                >
                  <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    placeholder="请输入免费观看时长（秒）"
                    addonAfter="秒"
                  />
                </Form.Item>
              </Card>

              {/* 操作按钮 */}
              <div style={{ textAlign: 'right', marginTop: 24 }}>
                <Space>
                  <Button icon={<ReloadOutlined />} onClick={handleResetPaymentConfig}>
                    重置
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    htmlType="submit"
                    loading={loading}
                  >
                    保存
                  </Button>
                </Space>
              </div>
            </Form>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default LiveStreamSettings;

